import { useEffect, useState } from "react"
import {
  getBmdPrereqCandidates,
  getBmdPrereqPreview,
} from "../services/bmd-prereq.service"
import type {
  BmdPrereqCandidate,
  BmdPrereqPreviewResponse,
} from "../types/bmd-prereq.types"

export type UseBmdPrereqCandidatesState = {
  candidates: BmdPrereqCandidate[]
  loading: boolean
  error: string | null
}

/** The student's base diagnostics, newest first. */
export function useBmdPrereqCandidates(
  studentKey: string,
): UseBmdPrereqCandidatesState {
  const [candidates, setCandidates] = useState<BmdPrereqCandidate[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!studentKey) {
      setCandidates([])
      setLoading(false)
      setError(null)
      return
    }

    const controller = new AbortController()
    setLoading(true)
    setError(null)

    getBmdPrereqCandidates(studentKey, controller.signal)
      .then((res) => {
        setCandidates(res.candidates)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load diagnostics")
        setCandidates([])
        setLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [studentKey])

  return { candidates, loading, error }
}

export type UseBmdPrereqPreviewState = {
  preview: BmdPrereqPreviewResponse | null
  loading: boolean
  error: string | null
}

/**
 * What the prerequisite test would contain for the selected diagnostic.
 *
 * Re-fetches whenever the selection changes. The in-flight request is aborted,
 * so a slow response for a diagnostic the tutor has moved on from cannot
 * overwrite the current one.
 */
export function useBmdPrereqPreview(
  diagnosticsSessionKey: string,
  studentKey: string,
): UseBmdPrereqPreviewState {
  const [preview, setPreview] = useState<BmdPrereqPreviewResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!diagnosticsSessionKey || !studentKey) {
      setPreview(null)
      setLoading(false)
      setError(null)
      return
    }

    const controller = new AbortController()
    setLoading(true)
    setError(null)

    getBmdPrereqPreview(
      {
        diagnostics_session_key: diagnosticsSessionKey,
        student_key: studentKey,
      },
      controller.signal,
    )
      .then((res) => {
        setPreview(res)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load the prerequisite test")
        setPreview(null)
        setLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [diagnosticsSessionKey, studentKey])

  return { preview, loading, error }
}
